import React, { useState, useEffect, type CSSProperties } from 'react';
import type { AuthorCalendarOverview } from '../types';
import { authorApi } from '@/features/workspace/api';
import {
  CloseIcon,
  PlusIcon,
  CheckIcon,
} from './AuthorIcons';

interface Props {
  onClose: () => void;
}

const EVENT_META: Record<string, { label: string; color: string }> = {
  SUBMISSION_DEADLINE: { label: 'Подача статьи', color: '#ff7b65' },
  REVIEW_MILESTONE: { label: 'Рецензирование', color: '#e2ad3a' },
  PAYMENT_DEADLINE: { label: 'Оплата договора', color: '#9e80ff' },
  COORDINATION_MEETING: { label: 'Совещание', color: '#66b7e8' },
  PUBLICATION_DATE: { label: 'Выход номера', color: '#49ddb7' },
};

export const AuthorCalendarOverlay: React.FC<Props> = ({ onClose }) => {
  const [calendarData, setCalendarData] = useState<AuthorCalendarOverview | null>(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventType, setNewEventType] = useState<'SUBMISSION_DEADLINE' | 'REVIEW_MILESTONE' | 'PAYMENT_DEADLINE' | 'COORDINATION_MEETING' | 'PUBLICATION_DATE'>('SUBMISSION_DEADLINE');
  const [newEventDate, setNewEventDate] = useState('2026-09-20');
  const [newEventNotes, setNewEventNotes] = useState('');
  const [loading, setLoading] = useState(true);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const data = await authorApi.getCalendar();
      setCalendarData(data);
    } catch {} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendar();
  }, []);

  const handleCreateEvent = async () => {
    if (!newEventTitle.trim()) {
      alert('Укажите название контрольного события');
      return;
    }
    try {
      await authorApi.createCalendarEvent({
        title: newEventTitle,
        event_type: newEventType,
        event_date: newEventDate,
        notes: newEventNotes,
      });
      setShowAddEventModal(false);
      setNewEventTitle('');
      setNewEventNotes('');
      loadCalendar();
    } catch (err: any) {
      alert(err?.message || 'Ошибка создания события');
    }
  };

  const getEventBadge = (type: string) => {
    const s = EVENT_META[type] || { label: type, color: '#8faebb' };
    return (
      <span className="cabinet-badge" style={{ '--badge': s.color } as CSSProperties}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Календарь автора</small>
              <h2>Радар дедлайнов и публикационный фокус-план</h2>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" onClick={() => setShowAddEventModal(true)} className="cab-btn primary">
              <PlusIcon className="w-4 h-4" />
              Добавить веху
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-body">
          <div className="cabinet-kpis" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginBottom: 14 }}>
            <div className="cabinet-kpi" style={{ ['--accent' as string]: '#9e80ff' }}>
              <small>Ближайших дедлайнов</small>
              <strong>{calendarData?.upcoming_deadlines.length || 0}</strong>
            </div>
            <div className="cabinet-kpi" style={{ ['--accent' as string]: '#41d2ff' }}>
              <small>Всего контрольных точек</small>
              <strong>{calendarData?.events.length || 0}</strong>
            </div>
            <div className="cabinet-kpi" style={{ ['--accent' as string]: '#49ddb7' }}>
              <small>Недельный пульс</small>
              <strong style={{ fontSize: 20 }}>В графике</strong>
            </div>
          </div>

          <section className="cabinet-panel" style={{ marginBottom: 14 }}>
            <header>
              <div>
                <h3>Фокус-план на текущий месяц</h3>
              </div>
            </header>
            <div className="cabinet-panel-body">
              <div className="cabinet-actions">
                {calendarData?.monthly_focus.map((focus, i) => (
                  <span key={i} className="cabinet-badge" style={{ '--badge': '#9e80ff' } as CSSProperties}>
                    {focus}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section className="cabinet-panel">
            <header>
              <div>
                <h3>Все запланированные события</h3>
              </div>
            </header>
            <div className="cabinet-panel-body" style={{ display: 'grid', gap: 10 }}>
              {loading ? (
                <div className="cabinet-note">Загрузка календаря…</div>
              ) : calendarData?.events.length === 0 ? (
                <div className="cabinet-note">Событий не найдено</div>
              ) : (
                calendarData?.events.map((evt) => (
                  <article key={evt.id} className="cabinet-card" style={{ ['--accent' as string]: '#9e80ff' }}>
                    <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                      <span className="cabinet-badge" style={{ '--badge': '#9e80ff' } as CSSProperties}>
                        {evt.event_date}
                      </span>
                      {getEventBadge(evt.event_type)}
                      <span className="cabinet-spacer" />
                      {evt.is_completed ? (
                        <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                          <CheckIcon className="w-4 h-4" /> Выполнено
                        </span>
                      ) : null}
                    </div>
                    <h3>{evt.title}</h3>
                    {evt.notes ? <p>{evt.notes}</p> : null}
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      </div>

      {showAddEventModal && (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <h2>Добавить контрольную веху</h2>
              </div>
              <button type="button" className="cabinet-btn" onClick={() => setShowAddEventModal(false)} aria-label="Закрыть">
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Название события *</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={newEventTitle}
                    onChange={(e) => setNewEventTitle(e.target.value)}
                    placeholder="Например: Дедлайн правок по рецензии"
                  />
                </label>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Тип вехи</span>
                    <select
                      className="cabinet-select"
                      value={newEventType}
                      onChange={(e) => setNewEventType(e.target.value as typeof newEventType)}
                    >
                      <option value="SUBMISSION_DEADLINE">Подача рукописи</option>
                      <option value="REVIEW_MILESTONE">Срок рецензирования</option>
                      <option value="PAYMENT_DEADLINE">Оплата договора/APC</option>
                      <option value="COORDINATION_MEETING">Координационное совещание</option>
                      <option value="PUBLICATION_DATE">Выпуск номера журнала</option>
                    </select>
                  </label>
                  <label className="cabinet-field">
                    <span>Дата</span>
                    <input
                      type="date"
                      className="cabinet-input"
                      value={newEventDate}
                      onChange={(e) => setNewEventDate(e.target.value)}
                    />
                  </label>
                </div>
                <label className="cabinet-field">
                  <span>Заметки / требования</span>
                  <textarea
                    className="cabinet-textarea"
                    value={newEventNotes}
                    onChange={(e) => setNewEventNotes(e.target.value)}
                    rows={3}
                  />
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setShowAddEventModal(false)}>
                  Отмена
                </button>
                <button type="button" className="cabinet-btn primary" onClick={handleCreateEvent}>
                  Сохранить событие
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
